import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Workforce } from "@/types/workforce";
import { Button } from "@/components/ui/button";
import { Plus, Building2, Calendar, DollarSign, MapPin, TrendingUp, ExternalLink, Briefcase, Coffee } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { AddWorkforceModal } from "@/components/workforce/AddWorkforceModal";
import { formatDate } from "@/utils/dateFormatter";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

interface CandidateAssignmentsTabProps {
	candidateId: string;
	candidateName: string;
}

export const CandidateAssignmentsTab = ({ candidateId, candidateName }: CandidateAssignmentsTabProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [assignments, setAssignments] = useState<Workforce[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);

	const fetchAssignments = async () => {
		try {
			setIsLoading(true);
			const { data, error } = await supabase
				.from('workforce')
				.select(`
					*,
					candidates!inner(id, name),
					jobs(id, title, companies(id, name))
				`)
				.eq('candidate_id', candidateId)
				.order('start_date', { ascending: false });

			if (error)
				throw error;

			if (data) {
				const transformed: Workforce[] = data.map((item: any) => ({
					...item,
					candidate: item.candidates,
					job: item.jobs ? {
						...item.jobs,
						company: item.jobs.companies
					} : undefined
				}));
				setAssignments(transformed);
			}
		} catch (error) {
			console.error("Error fetching assignments:", error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchAssignments();
	}, [candidateId]);

	const getStatusColor = (status: string) => {
		switch (status) {
			case 'active':
				return 'bg-green-100 text-green-700 border-green-200';
			case 'completed':
				return 'bg-blue-100 text-blue-700 border-blue-200';
			case 'terminated':
				return 'bg-red-100 text-red-700 border-red-200';
			case 'on_hold':
				return 'bg-yellow-100 text-yellow-700 border-yellow-200';
			case 'on_bench':
				return 'bg-purple-100 text-purple-700 border-purple-200';
			default:
				return 'bg-gray-100 text-gray-700 border-gray-200';
		}
	};

	const formatCurrency = (amount: number | null | undefined, currency: string = 'EUR') => {
		if (!amount) return '-';
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: currency || 'EUR',
			minimumFractionDigits: 0,
			maximumFractionDigits: 2
		}).format(amount);
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-8">
				<div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Header with Add Button */}
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
						<Briefcase className="h-5 w-5 text-gray-600 dark:text-gray-400" />
						{t("candidates.tabs.assignments", "Assignments")}
					</h3>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
						{t("candidates.tabsContent.assignments.description", "View and manage historical workforce assignments")}
					</p>
				</div>
				<Button
					onClick={() => setIsAddModalOpen(true)}
					className="bg-[#5a7ebf] hover:bg-[#5a7ebf]/90 text-white flex items-center gap-2"
				>
					<Plus className="h-4 w-4" />
					{t("workforce.addAssignment", "Add Assignment")}
				</Button>
			</div>

			{/* Assignments List */}
			{assignments.length === 0 ? (
				<Card className="border-dashed">
					<CardContent className="flex flex-col items-center justify-center py-12">
						<Briefcase className="h-12 w-12 text-gray-400 mb-4" />
						<p className="text-gray-500 text-center">
							{t("candidates.tabsContent.assignments.empty", "No assignments yet")}
						</p>
						<p className="text-gray-400 text-sm text-center mt-1">
							{t("candidates.tabsContent.assignments.emptyHint", "Create the first assignment for this candidate")}
						</p>
					</CardContent>
				</Card>
			) : (
				<div className="space-y-3">
					{assignments.map((assignment) => {
						const isBench = !!assignment.is_bench;
						return (
						<Card
							key={assignment.id}
							className={cn(
								"cursor-pointer hover:shadow-md transition-shadow",
								!isBench && assignment.status === 'active' && "border-green-200",
								isBench && "border-purple-300 bg-purple-50/40 dark:bg-purple-900/20 border-2"
							)}
							onClick={() => navigate(`/workforce/${assignment.id}`)}
						>
							<CardContent className="p-4">
								<div className="grid grid-cols-12 gap-4">
									{/* Job Info - 4 cols */}
									<div className="col-span-4 space-y-1">
										{isBench ? (
											<>
												<h4 className="font-semibold text-purple-700 flex items-center gap-2">
													<Coffee className="h-4 w-4" />
													{t("workforce.bench.onBench", "On Bench")}
												</h4>
												{assignment.bench_reason && (
													<p className="text-sm text-purple-600">
														{t(`workforce.bench.reasons.${assignment.bench_reason}`, assignment.bench_reason)}
													</p>
												)}
											</>
										) : assignment.job ? (
											<>
												<h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
													{assignment.job.title}
												</h4>
												<p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
													<Building2 className="h-3 w-3" />
													{assignment.job.company?.name || t("common.noCompany", "No company")}
												</p>
											</>
										) : (
											<>
												<h4 className="font-semibold text-gray-900 dark:text-gray-100">
													{assignment.external_job_title || t("workforce.externalJob", "External Assignment")}
												</h4>
												<p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
													<Building2 className="h-3 w-3" />
													{assignment.external_company_name || t("common.noCompany", "No company")}
												</p>
											</>
										)}
									</div>

									{/* Dates - 3 cols */}
									<div className="col-span-3 space-y-1">
										<div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
											<Calendar className="h-3 w-3" />
											<span>{formatDate(assignment.start_date)}</span>
										</div>
										{assignment.end_date ? (
											<div className="text-sm text-gray-500 dark:text-gray-400">
												{t("workforce.to", "to")} {formatDate(assignment.end_date)}
											</div>
										) : assignment.expected_end_date ? (
											<div className="text-sm text-gray-500 dark:text-gray-400">
												{t("workforce.expectedEnd", "exp.")} {formatDate(assignment.expected_end_date)}
											</div>
										) : (
											<div className="text-sm text-gray-500 dark:text-gray-400">
												{t("workforce.ongoing", "Ongoing")}
											</div>
										)}
									</div>

									{/* Rates - 3 cols */}
									<div className="col-span-3 space-y-1">
										{isBench ? (
											<>
												{assignment.bench_daily_cost && (
													<div className="flex items-center gap-1 text-sm text-purple-600">
														<DollarSign className="h-3 w-3" />
														<span>{formatCurrency(assignment.bench_daily_cost, assignment.currency)} /day</span>
														<span className="text-xs text-purple-500">(cost)</span>
													</div>
												)}
												<div className="text-xs text-purple-500">
												{t("workforce.bench.availableForAssignment", "Available for assignment")}
												</div>
											</>
										) : (
											<>
												{assignment.selling_price_daily && (
													<div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
														<DollarSign className="h-3 w-3" />
														<span>{formatCurrency(assignment.selling_price_daily, assignment.currency)} /day</span>
													</div>
												)}
												{assignment.location && (
													<div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
														<MapPin className="h-3 w-3" />
														<span>{assignment.location_type || assignment.location}</span>
													</div>
												)}
											</>
										)}
									</div>

									{/* Status - 2 cols */}
									<div className="col-span-2 flex items-start justify-end">
										<Badge className={cn("border", getStatusColor(assignment.status))}>
										{t(`workforce.statuses.${assignment.status}`, assignment.status)}
										</Badge>
									</div>
								</div>

								{/* Additional Info Row */}
									{!isBench && (assignment.contract_type || assignment.work_percentage) && (
										<div className="mt-3 pt-3 border-t dark:border-gray-700 flex gap-4 text-xs text-gray-500 dark:text-gray-400">
											{assignment.contract_type && (
												<span>{t(`workforce.contractType.${assignment.contract_type}`, assignment.contract_type)}</span>
											)}
											{assignment.work_percentage && (
												<span>{assignment.work_percentage}%</span>
											)}
										</div>
									)}
								</CardContent>
							</Card>
						);
					})}
				</div>
			)}

			{/* Add Assignment Modal */}
			<AddWorkforceModal
				open={isAddModalOpen}
				onOpenChange={setIsAddModalOpen}
				onSuccess={() => {
					setIsAddModalOpen(false);
					fetchAssignments();
				}}
			/>
		</div>
	);
};
